import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { AlertTriangle, ChevronRight, Loader2, RefreshCw } from "lucide-react";

// Compact "N events expiring this week" widget for the admin home.
// Clicking a bucket opens a drilldown with the actionable list so the
// admin can jump into comp / bulk-comp before hosts churn.
const BUCKETS = [
  { key: "expiring_24h", label: "in 24h", window: "24h", tone: "text-red-700 bg-red-50 border-red-200" },
  { key: "expiring_3d",  label: "in 3 days", window: "3d",  tone: "text-amber-800 bg-amber-50 border-amber-200" },
  { key: "expiring_7d",  label: "in 7 days", window: "7d",  tone: "text-blue-800 bg-blue-50 border-blue-200" },
  { key: "expired_recent", label: "past-due (30d)", window: "expired", tone: "text-navy-700 bg-cream-100 border-cream-200" },
];

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");

const ExpiryPredictorCard = ({ counts, onOpenBucket, loading }) => {
  if (!counts) {
    return (
      <div className="bg-white rounded-2xl soft-shadow p-5">
        <div className="text-navy-700/40 text-sm">Loading expiry snapshot…</div>
      </div>
    );
  }
  return (
    <div data-testid="expiry-predictor-card" className="bg-white rounded-2xl soft-shadow p-5">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-coral-300" />
        <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wider">Expiry radar</h3>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-navy-700/40" />}
      </div>
      <p className="text-xs text-navy-700/50 mb-3">
        Proactively comp before hosts churn. Tap a number to see the list.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            onClick={() => onOpenBucket(b.window, b.label)}
            data-testid={`expiry-bucket-${b.window}`}
            className={`text-left px-3 py-3 rounded-xl border transition-colors hover:shadow-sm ${b.tone}`}
          >
            <div className="text-2xl font-bold">{counts[b.key] ?? 0}</div>
            <div className="text-[11px] uppercase tracking-wider mt-0.5">{b.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Drawer that opens when a bucket is clicked.
const ExpiryDrawer = ({ open, window: win, label, onClose }) => {
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/admin/expiring-events?window=${encodeURIComponent(win)}`);
      setEvents(r.data.events || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load events.");
    } finally { setBusy(false); }
  }, [win]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="expiry-drawer" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-full max-w-lg h-full bg-white shadow-2xl overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-cream-200 px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-coral-300 font-semibold">Expiring</p>
            <h3 className="font-serif italic text-navy-700 text-2xl">{label} <span className="text-navy-700/40 text-base non-italic">· {events.length}</span></h3>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={load} disabled={busy} className="text-navy-700/60 hover:text-coral-300 p-1.5">
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onClose} data-testid="expiry-drawer-close" className="text-navy-700/60 hover:text-coral-300 text-2xl leading-none px-1">×</button>
          </div>
        </div>
        <div className="p-5">
          {err && <div className="bg-red-50 text-red-700 text-sm rounded-md px-3 py-2">{err}</div>}
          {!err && !busy && events.length === 0 && (
            <p className="text-sm text-navy-700/50 text-center py-10">Nothing in this window. Nice work.</p>
          )}
          <ul className="space-y-2">
            {events.map((e) => (
              <li key={e.id} className="bg-cream-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-navy-700 truncate">{e.name}</div>
                    <div className="text-[11px] text-navy-700/50 truncate">
                      {e.tier} · {e.paid ? "paid" : "unpaid"}{e.sponsored ? " · sponsored" : ""} · exp {fmtDate(e.expires_at)}
                    </div>
                    <div className="text-[11px] text-navy-700/50 truncate">
                      {e.host_email || <span className="italic">no host email</span>}{e.host_name ? ` · ${e.host_name}` : ""}
                    </div>
                    <div className="text-[10px] text-navy-700/40 font-mono truncate">{e.id}</div>
                  </div>
                  <button
                    onClick={() => {
                      // Scroll the admin's Sponsored panel into view + prefill event id.
                      const input = document.querySelector('[data-testid="sponsor-comp-event-id"]');
                      if (input) {
                        input.value = e.id;
                        input.dispatchEvent(new Event("input", { bubbles: true }));
                        input.scrollIntoView({ behavior: "smooth", block: "center" });
                      }
                      onClose();
                    }}
                    className="text-xs bg-coral-100 text-coral-500 px-3 py-1.5 rounded-full font-semibold hover:bg-coral-200 whitespace-nowrap inline-flex items-center gap-1"
                  >
                    Comp <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const ExpiryPredictor = ({ stats }) => {
  const [drawer, setDrawer] = useState({ open: false, window: null, label: "" });
  return (
    <>
      <ExpiryPredictorCard
        counts={stats?.expiry}
        loading={!stats}
        onOpenBucket={(win, label) => setDrawer({ open: true, window: win, label })}
      />
      <ExpiryDrawer
        open={drawer.open}
        window={drawer.window || "7d"}
        label={drawer.label || ""}
        onClose={() => setDrawer({ open: false, window: null, label: "" })}
      />
    </>
  );
};

export default ExpiryPredictor;
